import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { StorageService } from '../services';
import {
  parseBookingFiltersFromStorage,
  serializeBookingFilters,
} from '../filters';
import { APP } from '../config/app';

/**
 * Hydrates the URL from localStorage *before* the resolver runs, so the
 * data fetch happens only once with the proper query params.
 *
 * Without this guard, the page loads, resolver A fetches with default values,
 * then hydrate-in-component triggers a navigation, and resolver B fetches with
 * the stored values — two API calls per sidebar entry.
 *
 * Only activates on a truly bare URL (sidebar entry); on any URL with query
 * params the user (or share link) has expressed intent and we let it through.
 */
export const bookingsHydrationGuard: CanActivateFn = async (route, state) => {
  if (route.queryParamMap.keys.length > 0) return true;

  const storage = inject(StorageService);
  const router = inject(Router);

  const queryParams: { [key: string]: string | number } = {};

  const storedFilters = parseBookingFiltersFromStorage(await storage.getBookingsFilters());
  if (storedFilters) {
    const serialized = serializeBookingFilters(storedFilters);
    for (const [k, v] of Object.entries(serialized)) {
      if (v != null && v !== '') queryParams[k] = v as string;
    }
  }

  const storedSize = await storage.getBookingsPageSize();
  if (storedSize && storedSize > 0 && storedSize !== APP.DEFAULT_PAGE_SIZE) {
    queryParams['size'] = storedSize;
  }

  if (Object.keys(queryParams).length === 0) return true;

  // Redirect to URL with hydrated params. The resolver runs once with the proper URL.
  const path = state.url.split('?')[0];
  return router.createUrlTree([path], { queryParams });
};
